/**
 * useProfileForm Hook
 *
 * Custom hook for managing profile form state
 * Handles form fields, validation errors, and avatar upload
 */

import { useState, useEffect } from 'react';
import type { ContractorProfile } from '@hummii/shared-types';
import type { ClientProfileResponse } from '../../../api/clientProfile';

export interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  // Separate location fields
  address: string;
  city: string;
  province: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
  serviceArea: string;
  showLocation: boolean;
  profileDescription: string;
  hourlyRate: string;
  selectedAvatar: File | null;
  avatarUrl: string;
}

export interface UseProfileFormOptions {
  currentUser?: any;
  profileData?: ContractorProfile | null;
  clientProfileData?: ClientProfileResponse | null;
  isContractorMode: boolean;
  isClientDataLoading?: boolean;
}

export function useProfileForm({
  currentUser,
  profileData,
  clientProfileData,
  isContractorMode,
  isClientDataLoading = false,
}: UseProfileFormOptions) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Separate location fields
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [serviceArea, setServiceArea] = useState('');
  const [showLocation, setShowLocation] = useState(false);
  const [profileDescription, setProfileDescription] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Track if initial data was loaded to avoid overwriting user edits on refetch
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);

  // Load profile data when it becomes available (only on initial load)
  useEffect(() => {
    // Skip if already loaded to avoid overwriting user edits on refetch
    if (isInitialLoadDone) {
      return;
    }

    // Contractor mode - load from /contractors/me API
    if (isContractorMode && profileData) {
      // displayName is used for profile editing (First + Last name)
      // name is the legal/registered name (not editable from profile)
      const displayName = (profileData.user as any)?.displayName || profileData.user?.name || '';
      const nameParts = displayName.split(' ') || [];
      const loadedFirstName = nameParts[0] || '';
      const loadedLastName = nameParts.slice(1).join(' ') || '';

      setFirstName(loadedFirstName);
      setLastName(loadedLastName);
      setEmail(currentUser?.email || '');
      // @ts-ignore - phone exists in full User type
      setPhone(profileData.user?.phone || '');

      // Mark initial load as done
      setIsInitialLoadDone(true);

      // NOTE: Don't show validation errors on initial load - only on save attempt
      // This provides better UX as user can fill in missing fields before saving

      // Load separate location fields
      setAddress(profileData.address || '');
      setCity(profileData.city || '');
      setProvince(profileData.province || '');
      setPostalCode(profileData.postalCode || '');
      setLatitude(profileData.latitude);
      setLongitude(profileData.longitude);

      setServiceArea(`${profileData.serviceRadius || 20} km`);
      setProfileDescription(profileData.bio || '');
      setHourlyRate(profileData.hourlyRate?.toString() || '');
      // @ts-ignore - showLocation will be added to type
      setShowLocation(profileData.showLocation || false);

      // Load avatar URL from profileData
      const loadedAvatarUrl = profileData.avatarUrl || (profileData.user as any)?.avatarUrl || '';
      setAvatarUrl(loadedAvatarUrl);
    }

    // Client mode - load from /clients/me API
    if (!isContractorMode && clientProfileData) {
      // Parse displayName into first/last name
      const nameParts = clientProfileData.displayName?.split(' ') || [];
      const loadedFirstName = nameParts[0] || '';
      const loadedLastName = nameParts.slice(1).join(' ') || '';

      setFirstName(loadedFirstName);
      setLastName(loadedLastName);
      setEmail(clientProfileData.user?.email || currentUser?.email || '');
      setPhone(clientProfileData.phone || '');

      // Mark initial load as done
      setIsInitialLoadDone(true);

      // Load location data from client profile
      setAddress(clientProfileData.address || '');
      setCity(clientProfileData.city || '');
      setProvince(clientProfileData.province || '');
      setPostalCode(clientProfileData.postalCode || '');
      setLatitude(clientProfileData.latitude);
      setLongitude(clientProfileData.longitude);
      setServiceArea(`${clientProfileData.notificationRadius || 20} km`);
      setShowLocation(clientProfileData.showLocation || false);
      setProfileDescription(clientProfileData.bio || '');

      // Load avatar URL from clientProfileData
      setAvatarUrl(clientProfileData.avatarUrl || '');
    } else if (currentUser && !isContractorMode && !clientProfileData && !isClientDataLoading) {
      // Client mode fallback - load from currentUser (legacy)
      // Only use fallback when API request is done and returned no data
      const nameParts = currentUser.name?.split(' ') || [];
      const loadedFirstName = nameParts[0] || '';
      const loadedLastName = nameParts.slice(1).join(' ') || '';

      setFirstName(loadedFirstName);
      setLastName(loadedLastName);
      setEmail(currentUser.email || '');
      // @ts-ignore - phone may exist in full User type
      setPhone(currentUser.phone || '+1');

      // Mark initial load as done
      setIsInitialLoadDone(true);

      // Load location data if available (from currentUser as fallback)
      // @ts-ignore - these fields may exist in extended user type
      setAddress(currentUser.address || '');
      // @ts-ignore
      setCity(currentUser.city || '');
      // @ts-ignore
      setProvince(currentUser.province || '');
      // @ts-ignore
      setPostalCode(currentUser.postalCode || '');
      // @ts-ignore
      setLatitude(currentUser.latitude ?? null);
      // @ts-ignore
      setLongitude(currentUser.longitude ?? null);
      // @ts-ignore
      setServiceArea(currentUser.serviceArea || '20 km');
      // @ts-ignore
      setShowLocation(currentUser.showLocation || false);
      // @ts-ignore
      setProfileDescription(currentUser.bio || '');

      // Load avatar URL from currentUser
      setAvatarUrl(currentUser.avatarUrl || '');
    }
  }, [profileData, clientProfileData, currentUser, isContractorMode, isInitialLoadDone, isClientDataLoading]);

  const clearValidationError = (field: string) => {
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const formData: ProfileFormData = {
    firstName,
    lastName,
    email,
    phone,
    address,
    city,
    province,
    postalCode,
    latitude,
    longitude,
    serviceArea,
    showLocation,
    profileDescription,
    hourlyRate,
    selectedAvatar,
    avatarUrl,
  };

  return {
    formData,
    isFormDataLoaded: isInitialLoadDone,
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    setAddress,
    setCity,
    setProvince,
    setPostalCode,
    setLatitude,
    setLongitude,
    setServiceArea,
    setShowLocation,
    setProfileDescription,
    setHourlyRate,
    setSelectedAvatar,
    setAvatarUrl,
    validationErrors,
    setValidationErrors,
    clearValidationError,
  };
}
